import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Country, State, City } from "country-state-city";
import {
  IoAdd,
  IoArrowBack,
  IoBusiness,
  IoCall,
  IoCalendar,
  IoChevronDown,
  IoMail,
  IoPencil,
  IoPerson,
  IoTrash,
  IoEye,
} from "react-icons/io5";
import {
  MdFileUpload,
  MdDescription,
  MdTableChart,
  MdPictureAsPdf,
  MdImage,
} from "react-icons/md";

import Card from "components/card";
import CareOfStatusBadge from "components/badges/CareOfStatusBadge";
import {
  getCareOfById,
  updateCareOf,
  uploadCareOfDocuments,
  deleteCareOfDocument,
} from "views/admin/careOfManagement/careOfApi";

const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

const formatDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const formatDateTimeInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const normalize = (item) => ({
  ...item,
  id: item?._id || item?.id,
  CareOfType: item?.CareOfType || item?.careOfType,
});

const buildInitialForm = (item) => ({
  name: item?.name || "",
  email: item?.email || "",
  phone: item?.phone || "",
  houseNameOrNumber: item?.houseNameOrNumber || "",
  localityOrLandmark: item?.localityOrLandmark || "",
  pinCode: item?.pinCode || "",
  district: item?.district || "",
  country: item?.country || "",
  state: item?.state || "",
  leadId: item?.leadId || "",
  company: item?.company || "",
  status: (item?.status || "active").toLowerCase(),
  priority: item?.priority || "Medium",
  CareOfType: (item?.CareOfType || "").toLowerCase(),
  totalRevenue: item?.totalRevenue
    ? String(item.totalRevenue).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    : "",
  source: item?.source || "",
  convertedOn: formatDateInput(item?.convertedOn),
  nextFollowUpAt: formatDateTimeInput(item?.nextFollowUpAt),
  lastContactedAt: formatDateTimeInput(item?.lastContactedAt),
  reasonForLost: item?.reasonForLost || "",
  profileImage: null,
});

const InfoRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-gray-100 py-3 last:border-b-0 dark:border-white/10">
    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
      {label}
    </p>
    <p className="text-right text-sm font-semibold text-navy-700 dark:text-white">
      {value || "-"}
    </p>
  </div>
);

const CareOfProfile = () => {
  // Helper function to check if form has changed
  const hasFormChanged = (initial, current) => {
    return Object.keys(initial).some((key) => initial[key] !== current[key]);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { careOfId } = useParams();

  const [careOf, setCareOf] = useState(() =>
    normalize(location.state?.careOf || {})
  );
  const [form, setForm] = useState(() =>
    buildInitialForm(location.state?.careOf || {})
  );
  const [initialForm, setInitialForm] = useState(() =>
    buildInitialForm(location.state?.careOf || {})
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [selectedProfileImageName, setSelectedProfileImageName] = useState("");
  const [newNote, setNewNote] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [deleteNoteConfirm, setDeleteNoteConfirm] = useState({
    show: false,
    index: null,
  });
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editingNoteValue, setEditingNoteValue] = useState("");
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchByDropdown, setSearchByDropdown] = useState({});
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [deleteDocumentConfirm, setDeleteDocumentConfirm] = useState({
    show: false,
    documentId: null,
    fileName: null,
  });
  const previousBodyOverflow = useRef("");
  const dropdownRef = useRef();
  const optionRefs = useRef({});
  const fileInputRef = useRef();

  useEffect(() => {
    if (deleteDocumentConfirm.show) {
      previousBodyOverflow.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousBodyOverflow.current || "";
      };
    }

    document.body.style.overflow = previousBodyOverflow.current || "";
    return () => {
      document.body.style.overflow = previousBodyOverflow.current || "";
    };
  }, [deleteDocumentConfirm.show]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = (key, disabled = false, value, options) => {
    if (disabled) return;
    setOpenDropdown((prev) => {
      const next = prev === key ? null : key;
      if (next === key) {
        optionRefs.current = {};
        requestAnimationFrame(() => scrollSelectedOptionIntoView(key, value, options));
      }
      return next;
    });
    setSearchByDropdown((prev) => ({ ...prev, [key]: "" }));
  };

  const getFilteredOptions = (key, options) => {
    const term = (searchByDropdown[key] || "").trim().toLowerCase();
    if (!term) return options;

    // Handle both string array and object array formats
    const isStringArray = options.every((opt) => typeof opt === "string");
    return options.filter((option) => {
      const optionText = isStringArray ? option : option.label;
      return optionText.toLowerCase().includes(term);
    });
  };

  const scrollSelectedOptionIntoView = (keyName, value, options) => {
    const selectedKey = `${keyName}-${value}`;
    const element = optionRefs.current[selectedKey];
    if (element?.scrollIntoView) {
      element.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  };

  const renderDropdown = ({ keyName, label, value, options, onSelect, onClear, disabled }) => {
    const filteredOptions = getFilteredOptions(keyName, options);

    // Handle both string array and object array formats
    const isStringArray = options.every(opt => typeof opt === 'string');
    const displayValue = isStringArray
      ? (value || `Select ${label.toLowerCase()}`)
      : options.find((opt) => opt.value === value)?.label || (value ? `Select ${label.toLowerCase()}` : `Select ${label.toLowerCase()}`);

    return (
      <div className="relative w-full">
        <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
          {label}
        </label>
        <button
          type="button"
          onClick={() => toggleDropdown(keyName, disabled, value, filteredOptions)}
          className={`flex h-10 w-full items-center justify-between rounded-lg border px-3 text-left text-sm dark:text-white ${disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-white/10 dark:bg-gray-800/70 dark:text-gray-500"
            : "border-gray-200 bg-white hover:border-gray-300 dark:border-white/10 dark:bg-white/5"
            }`}
        >
          <span className="truncate">{displayValue}</span>
          <IoChevronDown className="h-4 w-4 text-gray-500" />
        </button>

        {openDropdown === keyName && !disabled && (
          <div className="absolute top-full z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800">
            <input
              type="text"
              value={searchByDropdown[keyName] || ""}
              onChange={(e) =>
                setSearchByDropdown((prev) => ({ ...prev, [keyName]: e.target.value }))
              }
              placeholder={`Search ${label.toLowerCase()}`}
              className="mb-2 h-9 w-full rounded-md border border-gray-200 px-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-gray-700 dark:text-white"
            />
            <button
              type="button"
              onClick={() => {
                onClear();
                setOpenDropdown(null);
              }}
              className="mb-1 h-8 w-full rounded-md px-2 text-left text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Clear
            </button>
            <div className="max-h-44 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">No options found</p>
              ) : (
                filteredOptions.map((option) => {
                  const optionValue = isStringArray ? option : option.value;
                  const optionLabel = isStringArray ? option : option.label;
                  const optionKey = `${keyName}-${optionValue}`;

                  return (
                    <button
                      ref={(el) => {
                        if (el) {
                          optionRefs.current[optionKey] = el;
                        }
                      }}
                      type="button"
                      key={optionValue}
                      onClick={() => {
                        onSelect(optionValue);
                        setOpenDropdown(null);
                      }}
                      className={`h-8 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${value === optionValue
                        ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                        : "text-gray-700 dark:text-gray-200"
                        }`}
                    >
                      {optionLabel}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!toast.show) return undefined;
    const timer = setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3000);
    return () => clearTimeout(timer);
  }, [toast.show]);

  useEffect(() => {
    const fetchCareOf = async () => {
      if (!careOfId) {
        setLoading(false);
        setError("CareOf ID is missing");
        return;
      }

      setLoading(true);
      setError("");

      try {
        const rawResult = await getCareOfById(careOfId);
        const result = normalize(rawResult);

        setCareOf(result);
        const initialFormData = buildInitialForm(result);
        setForm(initialFormData);
        setInitialForm(initialFormData);
        setImagePreview(result?.profileImage || "");
        setSelectedProfileImageName(getFileNameFromUrl(result?.profileImage) || "");
      } catch (fetchError) {
        console.error("[CareOf Profile] Fetch Error:", fetchError);
        setError(
          fetchError?.response?.data?.message ||
          "Failed to load care of profile"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCareOf();
  }, [careOfId]);

  const normalizeTotalRevenue = (value) => {
    const cleaned = value
      .replace(/,/g, "")
      .replace(/[^\d.]/g, "")
      .replace(/\.{2,}/g, ".");

    const [integerPartRaw = "", decimalPartRaw = ""] = cleaned.split(".");
    let integerPart = integerPartRaw.slice(0, 10);
    let decimalPart = decimalPartRaw.slice(0, 3);

    // Keep leading zero for decimals like `.12`
    if (!integerPart && decimalPart) {
      integerPart = "0";
    }

    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  };

  const FIELD_MAX_LENGTHS = {
    name: 100,
    email: 254,
    company: 100,
    houseNameOrNumber: 50,
    localityOrLandmark: 100,
    pinCode: 10,
    totalRevenue: 16,
  };

  const getCharacterCount = (value) => {
    return value?.length || 0;
  };

  const limitFieldValue = (key, value) => {
    if (key === "reasonForLost") return value.slice(0, 300);
    if (key === "totalRevenue") return value;
    const maxLength = FIELD_MAX_LENGTHS[key];
    if (!maxLength) return value;
    return value.slice(0, maxLength);
  };

  const limitChars = (value, maxChars = 300) => {
    return value.slice(0, maxChars);
  };

  const validateDateField = (key, value, lastContactedAtValue = "") => {
    if (!value) return ""; // Allow empty values

    const selectedDate = new Date(value);
    const now = new Date();

    if (key === "lastContactedAt") {
      // Should be past or present (<=now)
      if (selectedDate > now) {
        return "Last Contacted must be today or in the past";
      }
    } else if (key === "nextFollowUpAt") {
      if (lastContactedAtValue) {
        const lastContactedDate = new Date(lastContactedAtValue);
        if (selectedDate <= lastContactedDate) {
          return "Next Follow Up must be later than Last Contacted";
        }
      } else if (selectedDate <= now) {
        return "Next Follow Up must be a future date";
      }
    }

    return "";
  };

  const handleFieldChange = (key, value) => {
    if (key === "totalRevenue") {
      const formatted = normalizeTotalRevenue(value);
      setForm((prev) => ({ ...prev, [key]: formatted }));
    } else if (key === "reasonForLost") {
      const limitedValue = limitChars(value, 300);
      setForm((prev) => ({ ...prev, [key]: limitedValue }));
      if (limitedValue.length >= 300) {
        setFieldErrors((prev) => ({
          ...prev,
          [key]: "Maximum 300 characters reached",
        }));
      } else {
        setFieldErrors((prev) => ({ ...prev, [key]: "" }));
      }
    } else {
      const limitedValue = limitFieldValue(key, value);
      setForm((prev) => ({ ...prev, [key]: limitedValue }));
    }

    // Validate date fields
    if (key === "lastContactedAt" || key === "nextFollowUpAt") {
      const lastContactedValue = key === "lastContactedAt" ? value : form.lastContactedAt;
      const lastContactedError = validateDateField("lastContactedAt", key === "lastContactedAt" ? value : form.lastContactedAt);
      const nextFollowUpError = validateDateField(
        "nextFollowUpAt",
        key === "nextFollowUpAt" ? value : form.nextFollowUpAt,
        lastContactedValue
      );

      setFieldErrors((prev) => ({
        ...prev,
        lastContactedAt: lastContactedError,
        nextFollowUpAt: nextFollowUpError,
      }));
    } else if (key !== "reasonForLost") {
      setFieldErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const mapValidationErrors = (apiErrors) => {
    if (!Array.isArray(apiErrors)) return {};
    const next = {};
    apiErrors.forEach((item) => {
      if (item?.path && item?.msg) {
        const normalizedPath =
          item.path === "careOfType" ? "CareOfType" : item.path;
        next[normalizedPath] = item.msg;
      }
    });
    return next;
  };

  const getNoteValidationError = (apiErrors) => {
    const mapped = mapValidationErrors(apiErrors);
    const notePath = Object.keys(mapped).find((path) =>
      /^notes\[\d+\]\.note$/.test(path)
    );
    return notePath ? mapped[notePath] : "";
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    handleFieldChange("profileImage", file);

    if (file) {
      setSelectedProfileImageName(file.name);
      setImagePreview(URL.createObjectURL(file));
      return;
    }

    setSelectedProfileImageName(getFileNameFromUrl(careOf?.profileImage) || "");
    setImagePreview(careOf?.profileImage || "");
  };

  const handleSave = async () => {
    const careOfRecordId = careOf?._id || careOf?.id;
    if (!careOfRecordId) return;

    const normalizedInput = form.totalRevenue || "";
    const cleaned = normalizedInput
      .replace(/,/g, "")
      .replace(/[^\d.]/g, "")
      .replace(/\.{2,}/g, ".");
    const [integerPartRaw = "", decimalPartRaw = ""] = cleaned.split(".");
    const integerPart = integerPartRaw.slice(0, 10);
    const decimalPart = decimalPartRaw.slice(0, 3);

    if (integerPart.length > 10 || (decimalPart && decimalPart.length > 3)) {
      setFieldErrors((prev) => ({
        ...prev,
        totalRevenue: "Enter up to 10 digits and 2–3 decimal places",
      }));
      return;
    }

    // Validate date fields before save
    const lastContactedError = validateDateField("lastContactedAt", form.lastContactedAt);
    const nextFollowUpError = validateDateField(
      "nextFollowUpAt",
      form.nextFollowUpAt,
      form.lastContactedAt
    );

    if (lastContactedError || nextFollowUpError) {
      setFieldErrors((prev) => ({
        ...prev,
        lastContactedAt: lastContactedError,
        nextFollowUpAt: nextFollowUpError,
      }));
      return;
    }

    setIsUpdating(true);
    setError("");
    setFieldErrors({});

    try {
      const existingDocs = Array.isArray(careOf?.documents)
        ? careOf.documents
        : [];
      const allDocuments = [
        ...existingDocs,
        ...uploadedDocuments.map((doc) => doc.name),
      ];

      const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

      const totalRevenueString = form.totalRevenue || "";
      const cleanedTotalRevenue = totalRevenueString
        .replace(/,/g, "")
        .replace(/[^\d.]/g, "")
        .replace(/\.{2,}/g, ".");
      const [integerPartRaw = "", decimalPartRaw = ""] = cleanedTotalRevenue.split(".");
      const integerPart = integerPartRaw.slice(0, 10);
      const decimalPart = decimalPartRaw.slice(0, 3);
      const normalizedTotalRevenue = Number(
        `${integerPart}${decimalPart ? `.${decimalPart}` : ""}`
      );

      const payload = {
        ...form,
        status: capitalize(form.status),
        priority: form.priority,
        CareOfType: capitalize(form.CareOfType),
        totalRevenue: Number.isNaN(normalizedTotalRevenue) ? 0 : normalizedTotalRevenue,
        source: form.source || "",
        reasonForLost: form.reasonForLost,
        notes: careOf?.notes || [],
        documents: allDocuments,
      };

      const updated = normalize(await updateCareOf(careOfRecordId, payload));

      // Preserve associated leads and clients from current state if they exist
      const preservedLeads = careOf?.associatedLeads || careOf?.leads || [];
      const preservedClients = careOf?.associatedClients || careOf?.clients || [];

      const finalCareOf = {
        ...updated,
        associatedLeads: updated?.associatedLeads || updated?.leads || preservedLeads,
        associatedClients: updated?.associatedClients || updated?.clients || preservedClients,
      };

      setCareOf(finalCareOf);
      const initialFormData = buildInitialForm(finalCareOf);
      setForm(initialFormData);
      setInitialForm(initialFormData);
      setImagePreview(finalCareOf?.profileImage || imagePreview);
      setSelectedProfileImageName(getFileNameFromUrl(finalCareOf?.profileImage) || "");
      setUploadedDocuments([]);
      showToast("CareOf updated successfully", "success");
    } catch (updateError) {
      const apiErrors = updateError?.response?.data?.errors;
      const mapped = mapValidationErrors(apiErrors);
      if (mapped.CareOfType) {
        setFieldErrors(mapped);
        showToast("CareOf Type is required", "error");
        return;
      }
      if (Object.keys(mapped).length > 0) {
        setFieldErrors(mapped);
        return;
      }
      showToast(
        updateError?.response?.data?.message || "Failed to update care of",
        "error"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddNote = async () => {
    const careOfRecordId = careOf?._id || careOf?.id;
    if (!careOfRecordId || !newNote.trim()) return;

    setIsUpdating(true);
    try {
      const nextNotes = [
        ...(Array.isArray(careOf?.notes) ? careOf.notes : []),
        {
          date: new Date().toISOString(),
          note: newNote.trim(),
        },
      ];

      const updated = normalize(
        await updateCareOf(careOfRecordId, { notes: nextNotes })
      );

      // Preserve associated leads and clients
      const finalCareOf = {
        ...updated,
        associatedLeads: updated?.associatedLeads || updated?.leads || careOf?.associatedLeads || careOf?.leads || [],
        associatedClients: updated?.associatedClients || updated?.clients || careOf?.associatedClients || careOf?.clients || [],
      };

      setCareOf(finalCareOf);
      setNewNote("");
      showToast("Note added successfully", "success");
    } catch (noteError) {
      const apiErrors = noteError?.response?.data?.errors;
      const noteMsg = getNoteValidationError(apiErrors);
      if (noteMsg) {
        setFieldErrors((prev) => ({ ...prev, newNote: noteMsg }));
      }
      showToast(
        noteError?.response?.data?.message || "Failed to add note",
        "error"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteNote = async (index) => {
    const careOfRecordId = careOf?._id || careOf?.id;
    if (!careOfRecordId || !Array.isArray(careOf?.notes)) return;

    setIsUpdating(true);
    try {
      const nextNotes = careOf.notes.filter(
        (_, itemIndex) => itemIndex !== index
      );
      const updated = normalize(
        await updateCareOf(careOfRecordId, { notes: nextNotes })
      );

      // Preserve associated leads and clients
      const finalCareOf = {
        ...updated,
        associatedLeads: updated?.associatedLeads || updated?.leads || careOf?.associatedLeads || careOf?.leads || [],
        associatedClients: updated?.associatedClients || updated?.clients || careOf?.associatedClients || careOf?.clients || [],
      };

      setCareOf(finalCareOf);
      showToast("Note deleted successfully", "success");
    } catch (noteError) {
      showToast(
        noteError?.response?.data?.message || "Failed to delete note",
        "error"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const openDeleteNoteConfirm = (index) => {
    setDeleteNoteConfirm({ show: true, index });
  };

  const closeDeleteNoteConfirm = () => {
    if (isUpdating) return;
    setDeleteNoteConfirm({ show: false, index: null });
  };

  const confirmDeleteNote = async () => {
    if (
      deleteNoteConfirm.index === null ||
      deleteNoteConfirm.index === undefined
    )
      return;
    await handleDeleteNote(deleteNoteConfirm.index);
    setDeleteNoteConfirm({ show: false, index: null });
  };

  const handleStartEditNote = (index) => {
    if (!Array.isArray(careOf?.notes) || !careOf.notes[index]) return;
    setEditingNoteIndex(index);
    setEditingNoteValue(careOf.notes[index].note || "");
  };

  const handleCancelEditNote = () => {
    setEditingNoteIndex(null);
    setEditingNoteValue("");
  };

  const handleSaveNote = async (index) => {
    const careOfRecordId = careOf?._id || careOf?.id;
    if (!careOfRecordId || !Array.isArray(careOf?.notes) || editingNoteValue.trim() === careOf.notes[index]?.note) {
      handleCancelEditNote();
      return;
    }

    setIsUpdating(true);
    try {
      const nextNotes = careOf.notes.map((note, itemIndex) =>
        itemIndex === index
          ? { ...note, note: editingNoteValue.trim() }
          : note
      );

      const updated = normalize(
        await updateCareOf(careOfRecordId, { notes: nextNotes })
      );

      // Preserve associated leads and clients
      const finalCareOf = {
        ...updated,
        associatedLeads: updated?.associatedLeads || updated?.leads || careOf?.associatedLeads || careOf?.leads || [],
        associatedClients: updated?.associatedClients || updated?.clients || careOf?.associatedClients || careOf?.clients || [],
      };

      setCareOf(finalCareOf);
      handleCancelEditNote();
      showToast("Note updated successfully", "success");
    } catch (noteError) {
      const apiErrors = noteError?.response?.data?.errors;
      const noteMsg = getNoteValidationError(apiErrors);
      if (noteMsg) {
        setFieldErrors((prev) => ({ ...prev, editingNoteValue: noteMsg }));
      }
      showToast(
        noteError?.response?.data?.message || "Failed to update note",
        "error"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDocumentUpload = (event) => {
    const files = event.target.files;
    if (!files) return;

    const newDocs = Array.from(files).map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      file,
      id: `${file.name}-${file.size}-${Date.now()}`,
    }));

    setPendingDocuments((prev) => [...prev, ...newDocs]);
    event.target.value = "";
  };

  const handleRemoveDocument = (id) => {
    setPendingDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  const handleUploadDocuments = async () => {
    const careOfRecordId = careOf?._id || careOf?.id;
    if (!careOfRecordId || pendingDocuments.length === 0) return;

    setIsUploadingDocuments(true);
    try {
      const fileObjects = pendingDocuments.map((doc) => doc.file);
      const updated = normalize(
        await uploadCareOfDocuments(careOfRecordId, fileObjects)
      );

      // Preserve associated records
      const finalCareOf = {
        ...updated,
        associatedLeads: updated?.associatedLeads || updated?.leads || careOf?.associatedLeads || careOf?.leads || [],
        associatedClients: updated?.associatedClients || updated?.clients || careOf?.associatedClients || careOf?.clients || [],
      };

      setCareOf(finalCareOf);
      setPendingDocuments([]);
      showToast("Documents uploaded successfully", "success");
    } catch (docError) {
      showToast(
        docError?.response?.data?.message || "Failed to upload documents",
        "error"
      );
    } finally {
      setIsUploadingDocuments(false);
    }
  };

  const handleDeleteDocument = async (documentId, fileName) => {
    const careOfRecordId = careOf?._id || careOf?.id;
    if (!careOfRecordId || !documentId) return;

    setIsUpdating(true);
    try {
      const updated = normalize(
        await deleteCareOfDocument(careOfRecordId, documentId, fileName)
      );

      // Preserve associated records
      const finalCareOf = {
        ...updated,
        associatedLeads: updated?.associatedLeads || updated?.leads || careOf?.associatedLeads || careOf?.leads || [],
        associatedClients: updated?.associatedClients || updated?.clients || careOf?.associatedClients || careOf?.clients || [],
      };

      setCareOf(finalCareOf);
      setDeleteDocumentConfirm({ show: false, documentId: null, fileName: null });
      showToast("Document deleted successfully", "success");
    } catch (docError) {
      showToast(
        docError?.response?.data?.message || "Failed to delete document",
        "error"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const openDeleteDocumentConfirm = (documentId, fileName) => {
    setDeleteDocumentConfirm({ show: true, documentId, fileName });
  };

  const closeDeleteDocumentConfirm = () => {
    if (isUpdating) return;
    setDeleteDocumentConfirm({ show: false, documentId: null, fileName: null });
  };

  const confirmDeleteDocument = () => {
    handleDeleteDocument(deleteDocumentConfirm.documentId, deleteDocumentConfirm.fileName);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const getFileExtension = (fileName) => {
    if (typeof fileName !== "string") return "";
    return fileName.split(".").pop()?.toLowerCase() || "";
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return "";
    try {
      const parsed = new URL(url);
      return parsed.pathname.split("/").filter(Boolean).pop() || "";
    } catch (_) {
      return String(url).split("/").filter(Boolean).pop() || "";
    }
  };

  const getFileIconComponent = (fileName) => {
    const ext = getFileExtension(fileName);
    if (ext === "pdf") {
      return (
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/20">
          <MdPictureAsPdf className="h-6 w-6 text-red-500" />
        </span>
      );
    }
    if (["doc", "docx"].includes(ext)) {
      return (
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
          <MdDescription className="h-6 w-6 text-blue-500" />
        </span>
      );
    }
    if (["xls", "xlsx", "csv"].includes(ext)) {
      return (
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50 dark:bg-green-900/20">
          <MdTableChart className="h-6 w-6 text-green-600" />
        </span>
      );
    }
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
      return (
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50 dark:bg-purple-900/20">
          <MdImage className="h-6 w-6 text-purple-500" />
        </span>
      );
    }
    return (
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-900/20">
        <MdDescription className="h-6 w-6 text-gray-500" />
      </span>
    );
  };

  const isPreviewable = (fileName) => {
    const ext = getFileExtension(fileName);
    return ["jpg", "jpeg", "png", "gif", "webp", "pdf", "xlsx", "xls", "xlsm", "doc", "docx", "csv"].includes(ext);
  };



  const handlePreviewDocument = (doc) => {
    if (doc.url) {
      window.open(doc.url, "_blank");
    }
  };

  const getAllDocuments = () => {
    const existing = Array.isArray(careOf?.documents)
      ? careOf.documents.map((doc) => ({
        _id: doc._id,
        name: doc.fileName || doc.name,
        fileName: doc.fileName || doc.name,
        url: doc.url,
        size: doc.size,
        uploadedAt: doc.uploadedAt || doc.createdAt,
        isExisting: true,
      }))
      : [];
    const pending = pendingDocuments.map((doc) => ({
      id: doc.id,
      name: doc.name,
      fileName: doc.name,
      size: doc.size,
      isPending: true,
      file: doc.file,
    }));
    return [...existing, ...pending];
  };

  const getAssociatedLeads = () => {
    // Get combined list - try multiple possible property names from API
    const combinedList =
      careOf?.associatedRecords ||
      careOf?.records ||
      careOf?.associatedLeads ||
      careOf?.leads ||
      careOf?.clients ||
      careOf?.associatedClients ||
      [];

    // Ensure it's an array
    const allRecords = Array.isArray(combinedList) ? combinedList : [];

    // Filter records where isClient === false (these are leads)
    const leads = allRecords.filter((record) => {
      // Defensive handling: warn about missing isClient field
      if (record?.isClient === undefined) {
        console.warn(`[CareOf ${careOf?.id}] Record missing isClient field:`, record);
      }
      return record?.isClient === false;
    });

    return leads;
  };

  const getAssociatedClients = () => {
    // Get combined list - try multiple possible property names from API
    const combinedList =
      careOf?.associatedRecords ||
      careOf?.records ||
      careOf?.associatedLeads ||
      careOf?.leads ||
      careOf?.clients ||
      careOf?.associatedClients ||
      careOf?.associated_clients ||
      careOf?.relatedClients ||
      careOf?.linkedClients ||
      [];

    // Ensure it's an array
    const allRecords = Array.isArray(combinedList) ? combinedList : [];

    // Filter records where isClient === true (these are clients)
    const clients = allRecords.filter((record) => {
      // Defensive handling: warn about missing isClient field
      if (record?.isClient === undefined) {
        console.warn(`[CareOf ${careOf?.id}] Record missing isClient field:`, record);
      }
      return record?.isClient === true;
    });

    return clients;
  };

  // Country, State, District options for dropdowns
  const countryOptions = useMemo(
    () => Country.getAllCountries().map((country) => country.name),
    [],
  );
  const selectedCountry = useMemo(
    () => Country.getAllCountries().find((country) => country.name === form.country),
    [form.country],
  );
  const stateOptions = useMemo(() => {
    if (!selectedCountry) return [];
    return State.getStatesOfCountry(selectedCountry.isoCode).map((state) => state.name);
  }, [selectedCountry]);
  const selectedState = useMemo(() => {
    if (!selectedCountry || !form.state) return null;
    return State.getStatesOfCountry(selectedCountry.isoCode).find(
      (state) => state.name === form.state,
    );
  }, [selectedCountry, form.state]);
  const districtOptions = useMemo(() => {
    if (!selectedCountry || !selectedState) return [];
    const cities = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode);
    return Array.from(new Set(cities.map((city) => city.name))).sort();
  }, [selectedCountry, selectedState]);

  const fullAddress = useMemo(() => {
    const segments = [
      careOf?.houseNameOrNumber,
      careOf?.localityOrLandmark,
      careOf?.district,
      careOf?.state,
      careOf?.country,
      careOf?.pinCode,
    ].filter(Boolean);
    return segments.length ? segments.join(", ") : "-";
  }, [careOf]);

  if (loading) {
    return (
      <div className="mt-5 flex h-full items-center justify-center">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">
          Loading care of profile...
        </p>
      </div>
    );
  }

  if (!careOf?.id) {
    return (
      <div className="mt-5 flex h-full items-center justify-center">
        <p className="text-lg font-semibold text-red-600">
          CareOf profile not found
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 flex w-full flex-col gap-5">
      {toast.show && (
        <div
          className={`fixed right-6 top-20 z-[60] rounded-lg px-4 py-3 text-sm font-semibold shadow-md backdrop-blur-sm ${toast.type === "error"
            ? "border border-red-400/70 bg-red-500/20 text-red-700"
            : "border border-green-400/70 bg-green-500/20 text-green-700"
            }`}
        >
          {toast.message}
        </div>
      )}

      <button
        onClick={() => navigate(location.state?.from || "/admin/careof-management")}
        className="mt-2 flex items-center text-navy-700 hover:text-brand-500 dark:text-white dark:hover:text-brand-500"
        title="Back to CareOf Management"
      >
        <IoArrowBack className="text-2xl font-bold" />
      </button>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-stretch">
        <div className="col-span-1 flex h-full flex-col gap-5 lg:col-span-5">
          <Card extra="p-5">
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-brand-500/20 text-brand-500 ring-2 ring-brand-500/20">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="CareOf"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <IoPerson className="h-8 w-8" />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="truncate text-2xl font-bold text-navy-700 dark:text-white">
                  {careOf?.name || "-"}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-300">
                  ID: {careOf?.id || "-"}
                </p>
                <div className="mt-2">
                  <CareOfStatusBadge status={careOf?.status} />
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                <IoCall className="h-4 w-4" />
                <span>{careOf?.phone || "-"}</span>
              </div>
              <div className="flex items-center gap-3 break-all text-sm text-gray-600 dark:text-gray-300">
                <IoMail className="h-4 w-4" />
                <span>{careOf?.email || "-"}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                <IoBusiness className="h-4 w-4" />
                <span>{careOf?.company || "-"}</span>
              </div>
            </div>
          </Card>

          <Card extra="flex-1 p-5">
            <h4 className="mb-3 text-base font-bold text-navy-700 dark:text-white">
              Account Snapshot
            </h4>
            <div className="space-y-2 rounded-xl bg-gray-50 p-3 dark:bg-white/5">
              <InfoRow
                label="Type"
                value={careOf?.CareOfType ? careOf.CareOfType.charAt(0).toUpperCase() + careOf.CareOfType.slice(1) : "-"}
              />
              <InfoRow
                label="Revenue"
                value={
                  careOf?.totalRevenue?.toLocaleString?.() ||
                  careOf?.totalRevenue ||
                  "0"
                }
              />
              <InfoRow
                label="Created"
                value={formatDate(careOf?.createdAt, true)}
              />
              <InfoRow
                label="Modified"
                value={formatDate(careOf?.modifiedAt, true)}
              />
            </div>
          </Card>

          <Card extra="flex h-[400px] flex-col p-5">
            <h4 className="mb-3 text-base font-bold text-navy-700 dark:text-white">
              Associated Leads
            </h4>
            {getAssociatedLeads().length > 0 ? (
              <div className="flex-1 overflow-y-auto rounded-xl bg-gray-50 dark:bg-white/5">
                <div className="space-y-2 p-3">
                  {getAssociatedLeads().map((lead, index) => (
                    <div
                      key={lead?.id || lead?._id || index}
                      className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-white p-3 transition-all hover:border-gray-200 hover:shadow-sm dark:border-white/10 dark:bg-navy-800 dark:hover:border-white/20"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-semibold text-navy-700 dark:text-white">
                            {lead?.name || "-"}
                          </p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                            ID: {lead?.id || lead?._id || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {lead?.status && (
                          <span className="inline-flex rounded-full bg-brand-500/15 px-2 py-1 text-xs font-semibold text-brand-500 dark:bg-brand-500/20 dark:text-brand-300">
                            {lead.status}
                          </span>
                        )}
                        {lead?.phone && (
                          <span className="text-xs text-gray-600 dark:text-gray-300">
                            📱 {lead.phone}
                          </span>
                        )}
                      </div>
                      {lead?.createdAt && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Created: {formatDate(lead.createdAt, false)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center rounded-xl bg-gray-50 p-4 dark:bg-white/5">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  No associated leads found
                </p>
              </div>
            )}
          </Card>

          <Card extra="flex h-[300px] flex-col p-5">
            <h4 className="mb-3 text-base font-bold text-navy-700 dark:text-white">
              Associated Clients
            </h4>
            {getAssociatedClients().length > 0 ? (
              <div className="flex-1 overflow-y-auto rounded-xl bg-gray-50 dark:bg-white/5">
                <div className="space-y-2 p-3">
                  {getAssociatedClients().map((client, index) => (
                    <div
                      key={client?.id || client?._id || index}
                      className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-white p-3 transition-all hover:border-gray-200 hover:shadow-sm dark:border-white/10 dark:bg-navy-800 dark:hover:border-white/20"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-semibold text-navy-700 dark:text-white">
                            {client?.name || "-"}
                          </p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                            ID: {client?.id || client?._id || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {client?.status && (
                          <span className="inline-flex rounded-full bg-brand-500/15 px-2 py-1 text-xs font-semibold text-brand-500 dark:bg-brand-500/20 dark:text-brand-300">
                            {client.status}
                          </span>
                        )}
                        {client?.phone && (
                          <span className="text-xs text-gray-600 dark:text-gray-300">
                            📱 {client.phone}
                          </span>
                        )}
                      </div>
                      {client?.createdAt && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Created: {formatDate(client.createdAt, false)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center rounded-xl bg-gray-50 p-4 dark:bg-white/5">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  No associated clients found
                </p>
              </div>
            )}
          </Card>
        </div>
        <div className="col-span-1 flex h-full flex-col gap-5 lg:col-span-7">
          <Card extra="p-6">
            <h4 className="mb-1 text-xl font-bold text-navy-700 dark:text-white">
              Edit CareOf Details
            </h4>
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-300">
              Update profile information and save changes.
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Name
                </label>
                <input
                  value={form.name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                  placeholder="Enter name"
                  maxLength={100}
                  className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                />
                {fieldErrors.name && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Email
                </label>
                <input
                  value={form.email}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  placeholder="Enter email"
                  maxLength={254}
                  className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                />
                {fieldErrors.email && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Phone
                </label>
                <input
                  value={form.phone}
                  onChange={(e) =>
                    handleFieldChange(
                      "phone",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                  placeholder="Enter phone"
                  className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                />
                {fieldErrors.phone && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.phone}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Company
                </label>
                <input
                  value={form.company}
                  onChange={(e) => handleFieldChange("company", e.target.value)}
                  placeholder="Enter company"
                  maxLength={100}
                  className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                />
                {fieldErrors.company && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.company}</p>
                )}
              </div>

              <div>
                {renderDropdown({
                  keyName: "CareOfType",
                  label: "CareOf Type",
                  value: form.CareOfType,
                  options: [
                    { value: "individual", label: "Individual" },
                    { value: "business", label: "Business" },
                  ],
                  onSelect: (option) => handleFieldChange("CareOfType", option),
                  onClear: () => handleFieldChange("CareOfType", ""),
                  disabled: false,
                })}
                {fieldErrors.CareOfType && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.CareOfType}</p>
                )}
              </div>

              <div>
                {renderDropdown({
                  keyName: "priority",
                  label: "Priority",
                  value: form.priority,
                  options: [
                    { value: "Low", label: "Low" },
                    { value: "Medium", label: "Medium" },
                    { value: "High", label: "High" },
                    { value: "Urgent", label: "Urgent" },
                  ],
                  onSelect: (option) => handleFieldChange("priority", option),
                  onClear: () => handleFieldChange("priority", ""),
                  disabled: false,
                })}
              </div>

              <div>
                {renderDropdown({
                  keyName: "status",
                  label: "Status",
                  value: form.status,
                  options: [
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                    { value: "pending", label: "Pending" },
                    { value: "lost", label: "Lost" },
                  ],
                  onSelect: (option) => handleFieldChange("status", option),
                  onClear: () => handleFieldChange("status", ""),
                  disabled: false,
                })}
                {fieldErrors.status && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.status}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Total Revenue
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="^\d{0,10}(?:\.\d{0,3})?$"
                  value={form.totalRevenue}
                  onChange={(e) =>
                    handleFieldChange("totalRevenue", e.target.value)
                  }
                  placeholder="Enter revenue"
                  maxLength={16}
                  className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                />
                {fieldErrors.totalRevenue && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.totalRevenue}</p>
                )}
              </div>

              <div>
                {renderDropdown({
                  keyName: "source",
                  label: "Source",
                  value: form.source,
                  options: [
                    { value: "Website", label: "Website" },
                    { value: "Referral", label: "Referral" },
                    { value: "Ads", label: "Ads" },
                    { value: "Walk-In", label: "Walk-In" },
                    { value: "Other", label: "Other" },
                  ],
                  onSelect: (option) => handleFieldChange("source", option),
                  onClear: () => handleFieldChange("source", ""),
                  disabled: false,
                })}
                {fieldErrors.source && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.source}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  House Name / Number
                </label>
                <input
                  value={form.houseNameOrNumber}
                  onChange={(e) =>
                    handleFieldChange("houseNameOrNumber", e.target.value)
                  }
                  placeholder="Enter house name / number"
                  maxLength={50}
                  className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                />
                {fieldErrors.houseNameOrNumber && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.houseNameOrNumber}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Locality / Landmark
                </label>
                <input
                  value={form.localityOrLandmark}
                  onChange={(e) =>
                    handleFieldChange("localityOrLandmark", e.target.value)
                  }
                  placeholder="Enter locality / landmark"
                  maxLength={100}
                  className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                />
                {fieldErrors.localityOrLandmark && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.localityOrLandmark}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Pin Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.pinCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ""); // Allow only numbers
                    handleFieldChange("pinCode", value);
                  }}
                  placeholder="Enter pin code"
                  maxLength={6}
                  className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                />
                {fieldErrors.pinCode && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.pinCode}</p>
                )}
              </div>

              <div>
                {renderDropdown({
                  keyName: "country",
                  label: "Country",
                  value: form.country,
                  options: countryOptions.map((country) => ({
                    value: country,
                    label: country,
                  })),
                  onSelect: (option) => {
                    handleFieldChange("country", option);
                    // Reset state and district when country changes
                    handleFieldChange("state", "");
                    handleFieldChange("district", "");
                  },
                  onClear: () => {
                    handleFieldChange("country", "");
                    handleFieldChange("state", "");
                    handleFieldChange("district", "");
                  },
                  disabled: false,
                })}
                {fieldErrors.country && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.country}</p>
                )}
              </div>

              <div>
                {renderDropdown({
                  keyName: "state",
                  label: "State",
                  value: form.state,
                  options: stateOptions.map((state) => ({
                    value: state,
                    label: state,
                  })),
                  onSelect: (option) => {
                    handleFieldChange("state", option);
                    // Reset district when state changes
                    handleFieldChange("district", "");
                  },
                  onClear: () => {
                    handleFieldChange("state", "");
                    handleFieldChange("district", "");
                  },
                  disabled: !selectedCountry,
                })}
                {fieldErrors.state && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.state}</p>
                )}
              </div>

              <div>
                {renderDropdown({
                  keyName: "district",
                  label: "District",
                  value: form.district,
                  options: districtOptions.map((district) => ({
                    value: district,
                    label: district,
                  })),
                  onSelect: (option) => handleFieldChange("district", option),
                  onClear: () => handleFieldChange("district", ""),
                  disabled: !selectedState,
                })}
                {fieldErrors.district && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.district}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Next Follow Up
                </label>
                <input
                  type="datetime-local"
                  value={form.nextFollowUpAt}
                  onChange={(e) =>
                    handleFieldChange("nextFollowUpAt", e.target.value)
                  }
                  onFocus={() => setOpenDropdown(null)}
                  onClick={() => setOpenDropdown(null)}
                  className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                />
                {fieldErrors.nextFollowUpAt && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.nextFollowUpAt}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Last Contacted
                </label>
                <input
                  type="datetime-local"
                  value={form.lastContactedAt}
                  onChange={(e) =>
                    handleFieldChange("lastContactedAt", e.target.value)
                  }
                  onFocus={() => setOpenDropdown(null)}
                  onClick={() => setOpenDropdown(null)}
                  className="h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                />
                {fieldErrors.lastContactedAt && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.lastContactedAt}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Profile Image
                </label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <label
                      htmlFor="profileImage"
                      className="inline-flex h-11 items-center rounded-lg bg-brand-500 px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-600 cursor-pointer"
                    >
                      Choose File
                    </label>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Max image size: 10MB
                    </span>
                  </div>
                  <input
                    id="profileImage"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </div>
                {selectedProfileImageName && (
                  <p className="mt-1 text-xs text-gray-500">
                    {form.profileImage instanceof File ? "Selected file:" : "Current file:"} {selectedProfileImageName}
                  </p>
                )}
                {fieldErrors.profileImage && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.profileImage}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Reason For Lost
                </label>
                <textarea
                  value={form.reasonForLost}
                  onChange={(e) =>
                    handleFieldChange("reasonForLost", e.target.value)
                  }
                  rows={8}
                  placeholder="Enter reason for lost (if applicable)"
                  className="w-full rounded-lg border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                  style={{
                    maxHeight: "10rem",
                    minHeight: "10rem",
                    overflowY: "auto",
                    resize: "vertical",
                  }}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {getCharacterCount(form.reasonForLost)}/300 characters
                </p>
                {fieldErrors.reasonForLost && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.reasonForLost}</p>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={isUpdating || !hasFormChanged(initialForm, form)}
              className="mt-4 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {isUpdating ? "Updating..." : "Update"}
            </button>
          </Card>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <Card extra="col-span-1 p-5 lg:col-span-5">
              <h4 className="mb-3 text-lg font-bold text-navy-700 dark:text-white">
                Timeline
              </h4>
              <div className="rounded-xl bg-gray-50 p-4 dark:bg-white/5">
                {/* Next Follow Up Item */}
                <div className="pb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <IoCalendar className="h-5 w-5 flex-shrink-0 text-brand-500" />
                    <span className="text-lg font-bold text-navy-700 dark:text-white">
                      Next Follow Up
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 pl-8">
                    {formatDate(careOf?.nextFollowUpAt, true)}
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 dark:border-white/10 my-4"></div>

                {/* Last Contacted Item */}
                <div className="pt-4">
                  <div className="flex items-center gap-3 mb-2">
                    <IoCalendar className="h-5 w-5 flex-shrink-0 text-brand-500" />
                    <span className="text-lg font-bold text-navy-700 dark:text-white">
                      Last Contacted
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 pl-8">
                    {formatDate(careOf?.lastContactedAt, true)}
                  </div>
                </div>
              </div>
            </Card>

            <Card extra="col-span-1 p-5 lg:col-span-7">
              <h4 className="mb-3 text-lg font-bold text-navy-700 dark:text-white">
                Address Overview
              </h4>
              <p className="rounded-xl bg-gray-50 p-4 text-sm text-gray-700 dark:bg-white/5 dark:text-gray-200">
                {fullAddress}
              </p>

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <InfoRow label="Country" value={careOf?.country} />
                <InfoRow label="State" value={careOf?.state} />
                <InfoRow label="District" value={careOf?.district} />
                <InfoRow label="Pin Code" value={careOf?.pinCode} />
              </div>
            </Card>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <Card extra="col-span-1 p-5 lg:col-span-6">
          <h4 className="mb-3 text-lg font-bold text-navy-700 dark:text-white">
            Documents
          </h4>

          {/* Upload Zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleDocumentUpload({ target: { files: e.dataTransfer.files } });
            }}
            className="mb-4 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 py-6 px-4 transition-all hover:border-brand-400 hover:bg-gray-100 dark:border-white/10 dark:bg-white/5 dark:hover:border-brand-400 dark:hover:bg-white/10"
          >
            <MdFileUpload className="mb-2 h-8 w-8 text-brand-500 dark:text-brand-400" />
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">
              Click to select or drag & drop
            </p>
            <p className="text-xs font-semibold text-green-600 dark:text-green-300">
              Only PDF, DOCX, XLSX, JPG, JPEG, PNG, GIF, and WEBP files are allowed. Maximum file size: 5 MB.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleDocumentUpload}
              className="hidden"
              accept="*/*"
              disabled={isUploadingDocuments}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingDocuments}
              className="mt-2 rounded-lg bg-brand-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50 transition-all"
            >
              Select Files
            </button>
          </div>

          {/* Upload Button */}
          {pendingDocuments.length > 0 && (
            <div className="mb-4">
              <button
                type="button"
                onClick={handleUploadDocuments}
                disabled={isUploadingDocuments}
                className="w-full rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isUploadingDocuments ? "Uploading..." : "Upload Documents"}
              </button>
            </div>
          )}

          {/* Documents List */}
          {getAllDocuments().length > 0 ? (
            <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-white/10">
              <div className="space-y-2 p-3">
                {getAllDocuments().map((doc) => (
                  <div
                    key={doc._id || doc.id}
                    className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white p-3 transition-all hover:border-gray-200 hover:shadow-sm dark:border-white/10 dark:bg-navy-800 dark:hover:border-white/20"
                  >
                    {/* File Icon */}
                    {getFileIconComponent(doc.name)}

                    {/* File Info */}
                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate text-sm font-medium text-navy-700 dark:text-white"
                        title={doc.name}
                      >
                        {doc.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {doc.size ? formatBytes(doc.size) : ""}
                        {doc.uploadedAt && doc.size ? " • " : ""}
                        {doc.uploadedAt ? formatDate(doc.uploadedAt, false) : ""}
                      </p>
                      {doc.isPending && (
                        <p className="text-xs font-medium text-blue-600 dark:text-blue-400">
                          Pending upload
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1">
                      {doc.isExisting && isPreviewable(doc.name) && (
                        <button
                          type="button"
                          onClick={() => handlePreviewDocument(doc)}
                          disabled={isUpdating || isUploadingDocuments}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50 disabled:opacity-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                          title="Open in new tab"
                        >
                          <IoEye className="h-4 w-4" />
                        </button>
                      )}
                      {doc.isExisting && (
                        <button
                          type="button"
                          onClick={() => openDeleteDocumentConfirm(doc._id, doc.fileName)}
                          disabled={isUpdating || isUploadingDocuments}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                          title="Delete document"
                        >
                          <IoTrash className="h-4 w-4" />
                        </button>
                      )}
                      {doc.isPending && (
                        <button
                          type="button"
                          onClick={() => handleRemoveDocument(doc.id)}
                          disabled={isUploadingDocuments}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                          title="Remove from upload"
                        >
                          <IoTrash className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center dark:border-white/10 dark:bg-white/5">
              <MdDescription className="mx-auto mb-2 h-8 w-8 text-gray-400" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                No documents uploaded
              </p>
            </div>
          )}
        </Card>

        <Card extra="col-span-1 p-5 lg:col-span-6">
          <h4 className="mb-3 text-lg font-bold text-navy-700 dark:text-white">
            Notes
          </h4>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row">
            <div className="flex w-full flex-col gap-2">
              <textarea
                value={newNote}
                onChange={(event) => {
                  const nextValue = event.target.value.slice(0, 300);
                  setNewNote(nextValue);
                  setFieldErrors((prev) => ({ ...prev, newNote: "" }));
                }}
                rows={2}
                maxLength={300}
                placeholder="Write a new note"
                className="w-full resize-none rounded-lg border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
              />
              <p className="text-right text-xs text-gray-500">
                {newNote.length}/300 characters
              </p>
            </div>
            {fieldErrors.newNote && (
              <p className="text-xs text-red-500">{fieldErrors.newNote}</p>
            )}
            <button
              type="button"
              onClick={handleAddNote}
              disabled={isUpdating || !newNote.trim()}
              className="inline-flex min-w-[110px] h-10 items-center justify-center gap-2 rounded-xl border border-brand-400/40 bg-gradient-to-b from-brand-500 to-brand-600 px-3 py-0.5 text-sm font-semibold text-white shadow-sm shadow-brand-500/20 transition-all hover:from-brand-600 hover:to-brand-700 hover:shadow-md hover:shadow-brand-500/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <IoAdd className="h-4 w-4" />
              Add Note
            </button>
          </div>

          {Array.isArray(careOf?.notes) && careOf.notes.length > 0 ? (
            <div className="space-y-3">
              {careOf.notes.map((item, index) => (
                <div
                  key={`${item?.date || "note"}-${index}`}
                  className="rounded-xl border border-gray-100 p-3 dark:border-white/10"
                >
                  {editingNoteIndex === index ? (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(item?.date, true)}
                      </p>
                      <textarea
                        value={editingNoteValue}
                        onChange={(e) => setEditingNoteValue(e.target.value)}
                        rows={3}
                        className="w-full rounded-lg border border-gray-200 p-2 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                      />
                      {fieldErrors.editingNoteValue && (
                        <p className="text-xs text-red-500">{fieldErrors.editingNoteValue}</p>
                      )}
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          onClick={handleCancelEditNote}
                          disabled={isUpdating}
                          className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveNote(index)}
                          disabled={isUpdating || editingNoteValue.trim() === item?.note}
                          className="rounded-lg px-3 py-1.5 text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50"
                        >
                          {isUpdating ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(item?.date, true)}
                        </p>
                        <p className="mt-1 text-sm text-navy-700 dark:text-white">
                          {item?.note || "-"}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleStartEditNote(index)}
                          disabled={isUpdating}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50 disabled:opacity-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                          title="Edit note"
                        >
                          <IoPencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openDeleteNoteConfirm(index)}
                          disabled={isUpdating}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20"
                          title="Delete note"
                        >
                          <IoTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No notes available
            </p>
          )}
        </Card>
      </div>

      {error && (
        <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </p>
      )}

      {/* Delete Document Confirmation Modal */}
      {deleteDocumentConfirm.show && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={closeDeleteDocumentConfirm}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Delete Document?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This document will be permanently removed.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={closeDeleteDocumentConfirm}
                disabled={isUpdating}
                className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteDocument}
                disabled={isUpdating}
                className="w-full rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50 sm:w-auto"
              >
                {isUpdating ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteNoteConfirm.show && (
        <div
          className="bg-black/50 fixed inset-0 z-[70] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={closeDeleteNoteConfirm}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Delete Note?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This note will be permanently removed.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={closeDeleteNoteConfirm}
                disabled={isUpdating}
                className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteNote}
                disabled={isUpdating}
                className="w-full rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50 sm:w-auto"
              >
                {isUpdating ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareOfProfile;
